import on from 'dom-event';
import select from 'dom-select';
import { set, unset, contains } from 'utils/general';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import bus from 'lib/bus';

const bodyScrollLockConfig = {
  allowTouchMove: (el) => {
    while (el && el !== document.body) {
      if (el.getAttribute('body-scroll-lock-ignore') !== null) {
        return true;
      }

      el = el.parentElement;
    }
  },
};

export default (el) => {
  const navigation = select('.js-header-navigation.js-is-mobile', el);
  const hamburger = select('.js-hamburger', el);
  const closeIcon = select('.js-menu-close', el);
  const promo = select('.js-promo-bar', el);
  const searchButton = select('.js-toggle-search-bar');
  const searchBar = select('.js-search-bar', el);
  const countrySelector = select('.js-country-trigger', el);

  enableHamburgerClick(el, hamburger, navigation);
  enableCloseIconClick(el, closeIcon, navigation);
  enableSearchBarToggle(el, searchButton, searchBar, searchButton);
  enableCountrySelectorClick(el, countrySelector);
};

function setHeaderHeight(el) {
  document.body.style.setProperty(
    '--desktop-header-height',
    `${el.offsetHeight}px`,
  );
}

function enableCountrySelectorClick(el, countrySelector) {
  on(countrySelector, 'click', (e) => bus.$emit('show-country-selector-panel'));
}

function enableHamburgerClick(el, hamburger, navigation) {
  on(hamburger, 'click', (e) => openMobileMenu(e, navigation));
}

function enableCloseIconClick(el, closeIcon, navigation) {
  on(closeIcon, 'click', (e) => closeMobileMenu(e, navigation));
}

function enableSearchBarToggle(el, searchButton, searchBar) {
  on(searchButton, 'click', (e) =>
    toggleSearchBar(e, el, searchBar, searchButton),
  );
}

/**
 * Toggles the mobile menu by adding the appropriate class to the header
 *
 * @param {Object} e the click event
 * @param {DOM Node} el the header element
 * @param {DOM Node} searchBar the search bar element
 */
function toggleSearchBar(e, el, searchBar, searchButton) {
  if (contains(el, 'showing-search-overlay')) {
    unset(el, 'showing-search-overlay');
    clearAllBodyScrollLocks();
    searchButton.focus();
    el.querySelector('.search-bar').style.visibility = 'hidden';
  } else {
    set(el, 'showing-search-overlay');
    disableBodyScroll(searchBar, bodyScrollLockConfig);
    bus.$emit('showing-search-overlay');
    bus.$emit('focus-search-overlay');
    el.querySelector('.search-bar').style.visibility = 'visible';
  }
}

/**
 * Enables on functionality when
 * the hamburger icon is clicked, activating
 * the mobile menu.
 *
 * @param {Object} e the click event
 * @param {DOM Node} navigation
 */
function openMobileMenu(e, navigation) {
  const navInner = select('.js-nav-inner', navigation);
  const closeIcon = select('.js-menu-close', navigation);
  const focusableElements = navigation.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];
  
  // Set up focus trap
  navigation.addEventListener('keydown', function trapFocus(e) {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }
    if (e.key === 'Escape') {
      closeMobileMenu(navigation);
    }
  });
  
  navigation.previouslyFocused = document.activeElement;
  
  set(navigation, 'is-visible');
  document.body.classList.add('scroll-lock');
  disableBodyScroll(navInner, bodyScrollLockConfig);
  
  setTimeout(() => {
    closeIcon.focus();
  }, 500);
}

/**
 * Enables off functionality when
 * the close icon is clicked, deactivating
 * the mobile menu.
 *
 * @param {Object} e the click event
 * @param {DOM Node} close icon
 * @param {DOM Node} navigation
 */
function closeMobileMenu(e, navigation) {
  const navInner = select('.js-nav-inner', navigation);
  unset(navigation, 'is-visible');
  clearAllBodyScrollLocks();
  document.body.classList.remove('scroll-lock');
  if (navigation.previouslyFocused) {
    navigation.previouslyFocused.focus();
  }
}

function resetMobileNavStaes(navigation) {
  // Classes to reset
  const selectors = ['is-visible', 'no-scroll-y'];
  selectors.forEach((elmClass) => {
    const navs = select.all(`.${elmClass}`, navigation);

    // Reset all scrolls
    if (elmClass === 'is-visible') {
      navs.forEach((nav) => {
        const innerNav = select('.header-nav__inner', nav);
        nav.scrollTop = 0;
        if (innerNav) {
          innerNav.scrollTop = 0;
        }
      });
    }
    // unset classes
    unset(navs, elmClass);
  });
}
